<script setup lang="ts">
import { useDynamicHeight } from '@/composables/useDynamicHeight';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { type TaskRecord } from '@/api/task-records';
import { clinicalAuthorName } from '@/utils/clinical-author';
import { useShowVoided } from '@/composables/useShowVoided';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ClinicalRecordVoid from '@/components/clinical-records/clinical-record-void.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';

const props = defineProps<{ taskRecords: TaskRecord[] }>();

// Anulados show on demand, crossed out.
const { showVoided, voidedCount, visibleItems } = useShowVoided(() => props.taskRecords);

const { dynamicHeight } = useDynamicHeight();
const { tableProps } = useResponsiveTable();

const headers = [
  { title: 'Categoría', key: 'taskCategoryName', align: 'start' },
  { title: 'Tarea', key: 'taskName', align: 'start' },
  { title: 'Estado', key: 'aasmState', align: 'center' },
  { title: 'Programada', key: 'humanScheduledAt', align: 'center' },
  { title: 'Registrada', key: 'humanRecordedAt', align: 'center' },
  { title: 'Registrada por', key: 'author', align: 'start', sortable: false },
  { title: 'Observaciones', key: 'observations', align: 'start' },
] as const;

function getStateColor(state: string) {
  switch (state) {
  case 'completed': return 'success';
  case 'incomplete': return 'error';
  case 'pending': return 'warning';
  default: return 'grey';
  }
}

function getStateText(state: string) {
  switch (state) {
  case 'completed': return 'Completada';
  case 'incomplete': return 'Incompleta';
  case 'pending': return 'Pendiente';
  default: return state;
  }
}
</script>

<template>
  <v-data-table
    v-bind="tableProps"
    :headers="headers"
    :header-props="{ class: 'font-weight-bold' }"
    :items="visibleItems"
    :height="dynamicHeight"
    fixed-header
    item-key="id"
    class="pa-2"
    loading-text="Cargando registros de tareas..."
    no-data-text="No hay registros de tareas en este turno"
  >
    <template
      v-if="voidedCount > 0"
      #top
    >
      <div class="justify-end d-flex pa-2">
        <show-voided-switch
          v-model="showVoided"
          :count="voidedCount"
        />
      </div>
    </template>
    <template #[`item.taskCategoryName`]="{ item }">
      <v-chip
        color="primary"
        size="small"
        variant="outlined"
      >
        {{ item.taskCategoryName }}
      </v-chip>
    </template>
    <template #[`item.aasmState`]="{ item }">
      <v-chip
        :color="getStateColor(item.aasmState)"
        size="small"
        :class="{ 'text-decoration-line-through': item.voided }"
      >
        {{ getStateText(item.aasmState) }}
      </v-chip>
      <clinical-void-mark
        :voided="item.voided"
        :details="item.voidDetails"
        class="justify-center mt-1"
      />
    </template>
    <template #[`item.humanRecordedAt`]="{ item }">
      <span class="text-no-wrap">{{ item.humanRecordedAt || '-' }}</span>
      <!-- A Tarea pendiente is not a registro clínico until it is recorded. -->
      <clinical-record-history
        v-if="item.aasmState !== 'pending' || item.edited"
        item-type="TaskRecord"
        :item-id="item.id"
        :edited="item.edited"
        :label="`${item.taskName} · ${item.humanScheduledAt}`"
      />
      <clinical-record-void
        v-if="item.aasmState !== 'pending'"
        item-type="TaskRecord"
        :item-id="item.id"
        :can-void="item.canVoid"
        :label="`${item.taskName} · ${item.humanScheduledAt}`"
      />
    </template>
    <template #[`item.author`]="{ item }">
      {{ clinicalAuthorName(item.author) || '-' }}
    </template>
    <template #[`item.observations`]="{ item }">
      <p
        class="text-wrap"
        :class="{ 'text-decoration-line-through text-medium-emphasis': item.voided }"
      >
        {{ item.observations || '-' }}
      </p>
    </template>
  </v-data-table>
</template>
